import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import PageHeader from "../../components/common/PageHeader";
import Pagination from "../../components/Pagination";
import api from "../../utils/api";
import { useRestaurantConfig } from "../../utils/restaurantConfig";
import { getCurrencySymbol } from "../../helper/formatCurrency";

const labelClass =
  "text-xs font-semibold text-gray-500 uppercase tracking-widest mb-2 block";

const OtherAppSaleHistory = () => {
  const { appId } = useParams();
  const location = useLocation();
  const { locale, timezone } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);

  const [app, setApp] = useState(location.state?.app || null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [summary, setSummary] = useState({ totalSalesSum: 0, totalOrdersSum: 0 });
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const pageSize = 10;

  const resolvedAppName = app?.name;

  useEffect(() => {
    const resolveApp = async () => {
      if (!appId) return;
      if (location.state?.app && location.state.app._id === appId) {
        setApp(location.state.app);
        return;
      }
      try {
        const res = await api.get("/other-apps");
        const found = (res.data?.apps || []).find((a) => String(a._id) === appId);
        if (found) setApp(found);
        else toast.error("App not found");
      } catch (e) {
        console.error(e);
        toast.error("Failed to load app");
      }
    };
    resolveApp();
  }, [appId, location.state?.app]);

  const fetchSales = async ({ page = currentPage, start = startDate, end = endDate } = {}) => {
    if (!appId) return;
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
      });
      if (start) queryParams.append("startDate", start);
      if (end) queryParams.append("endDate", end);

      const res = await api.get(`/other-apps/${appId}/daily-sales?${queryParams.toString()}`);
      if (res.data?.success) {
        setSales(res.data.dailySales || []);
        setTotalPages(res.data?.pagination?.totalPages || 1);
        setTotalCount(res.data?.pagination?.totalCount || 0);
        setSummary(res.data?.summary || { totalSalesSum: 0, totalOrdersSum: 0 });

        const serverPage = res.data?.pagination?.page || page;
        if (serverPage !== currentPage) {
          setCurrentPage(serverPage);
        }
      } else {
        setSales([]);
        setTotalPages(1);
        setTotalCount(0);
        setSummary({ totalSalesSum: 0, totalOrdersSum: 0 });
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load sales history");
      setSales([]);
      setTotalPages(1);
      setTotalCount(0);
      setSummary({ totalSalesSum: 0, totalOrdersSum: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!appId) return;
    fetchSales({ page: currentPage, start: startDate, end: endDate });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when switching app route
  }, [appId, currentPage, startDate, endDate]);

  const beginEdit = (sale) => {
    setEditingId(sale._id);
    setDraft((prev) => ({
      ...prev,
      [sale._id]: {
        totalSale: String(sale.totalSale ?? ""),
        totalOrders: String(sale.totalOrders ?? ""),
      },
    }));
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const handleDraftChange = (id, field, value) => {
    const digitsOnly = field === "totalSale" || field === "totalOrders";
    const nextVal = digitsOnly ? value.replace(/\D/g, "") : value;
    setDraft((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [field]: nextVal },
    }));
  };

  const handleSaveRow = async (saleRow) => {
    const rowDraft = draft[saleRow._id] || {};
    if (!rowDraft.totalSale?.toString().trim()) {
      toast.error("Total sale cannot be empty");
      return;
    }
    if (!rowDraft.totalOrders?.toString().trim()) {
      toast.error("Total orders cannot be empty");
      return;
    }

    try {
      setSavingId(saleRow._id);
      const res = await api.put(`/other-apps/daily-sales/${saleRow._id}`, {
        totalSale: Number(rowDraft.totalSale),
        totalOrders: Number(rowDraft.totalOrders),
      });
      if (res.data?.success) {
        toast.success("Saved");
        setEditingId(null);
        await fetchSales();
      }
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to save";
      toast.error(msg);
    } finally {
      setSavingId(null);
    }
  };

  const confirmSoftDelete = async () => {
    if (!deleteTarget) return;
    try {
      setLoading(true);
      const res = await api.delete(`/other-apps/daily-sales/${deleteTarget.id}`);
      if (res.data?.success) {
        toast.success("Record removed");
        setDeleteTarget(null);
        if (editingId === deleteTarget.id) setEditingId(null);
        await fetchSales();
      }
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to remove record";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const formatSaleDate = (d) => {
    if (!d) return "—";
    const date = new Date(d);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
  };

  const formatMetaTime = (sale) => {
    const who = sale.updatedBy?.username || sale.createdBy?.username || "System";
    const when = new Date(sale.updatedAt || sale.createdAt).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: timezone && timezone !== "UTC" ? timezone : undefined,
    });
    return { who, when };
  };

  const emptyState = !loading && sales.length === 0;

  const getRestaurantToday = () => {
    const d = new Date();
    if (!timezone || timezone === "UTC") return d.toISOString().slice(0, 10);
    try {
      return new Intl.DateTimeFormat('en-CA', { 
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(d);
    } catch (e) {
      return d.toISOString().slice(0, 10);
    }
  };
  const today = getRestaurantToday();

  return (
    <div>
      <PageHeader
        showBackButton={true}
        backTo="/other-apps-sale"
        title={resolvedAppName ? `${resolvedAppName} — sales` : "App sales"}
        subtitle="Display app sale"
      />

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="mb-5 flex items-end gap-3 flex-wrap">
          <div className="w-full sm:w-[180px]">
            <label className={labelClass}>From</label>
            <input
              type="date"
              value={startDate}
              max={today}
              onChange={(e) => {
                setCurrentPage(1);
                setStartDate(e.target.value);
              }}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black focus:outline-none focus:border-black focus:bg-white transition-all duration-200"
            />
          </div>
          <div className="w-full sm:w-[180px]">
            <label className={labelClass}>To</label>
            <input
              type="date"
              value={endDate}
              max={today}
              onChange={(e) => {
                setCurrentPage(1);
                setEndDate(e.target.value);
              }}
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-black focus:outline-none focus:border-black focus:bg-white transition-all duration-200"
            />
          </div>
          {startDate || endDate ? (
            <button
              type="button"
              onClick={() => {
                setCurrentPage(1);
                setStartDate("");
                setEndDate("");
              }}
              className="h-[46px] px-4 rounded-xl text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all duration-200"
            >
              Clear
            </button>
          ) : null}
          <div className="ml-auto flex items-center gap-6 bg-gray-50 px-5 py-3 rounded-xl border border-gray-100">
            <div>
              <div className="text-xs text-gray-500 uppercase font-semibold tracking-wider">Total Sales</div>
              <div className="font-bold text-gray-900 text-lg">{currencySymbol}{summary.totalSalesSum?.toFixed(2) || "0.00"}</div>
            </div>
            <div className="w-px h-8 bg-gray-200"></div>
            <div>
              <div className="text-xs text-gray-500 uppercase font-semibold tracking-wider">Total Orders</div>
              <div className="font-bold text-gray-900 text-lg">{summary.totalOrdersSum || 0}</div>
            </div>
          </div>
        </div>

        <div className={labelClass}>Records</div>
        {loading && !sales.length ? (
          <div className="text-sm text-gray-500 py-6">Loading…</div>
        ) : emptyState ? (
          <div className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-xl p-6 text-center">
            No sale entries for this app yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="py-3 pr-4 font-semibold">Date</th>
                <th className="py-3 pr-4 font-semibold">Total sale</th>
                <th className="py-3 pr-4 font-semibold">Total orders</th>
                <th className="py-3 pr-0 font-semibold w-[160px]">Action</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((sale) => {
                const isEditing = editingId === sale._id;
                const rowDraft = draft[sale._id] || {};
                const { who, when } = formatMetaTime(sale);

                return (
                  <React.Fragment key={sale._id}>
                    <tr className="border-b border-gray-100 align-top">
                      <td className="py-3 pr-4 text-gray-900 font-medium whitespace-nowrap">
                        {formatSaleDate(sale.saleDate)}
                      </td>
                      <td className="py-3 pr-4">
                        {isEditing ? (
                          <div className="relative flex items-center max-w-[200px]">
                            <span className="absolute left-3 text-gray-500 select-none">{currencySymbol}</span>
                            <input
                              type="text"
                              value={rowDraft.totalSale ?? ""}
                              onChange={(e) => handleDraftChange(sale._id, "totalSale", e.target.value)}
                              className="w-full pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-black focus:outline-none focus:border-black focus:bg-white"
                              inputMode="numeric"
                            />
                          </div>
                        ) : (
                          <span className="font-semibold text-gray-900">
                            {currencySymbol}
                            {sale.totalSale}
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {isEditing ? (
                          <input
                            type="text"
                            value={rowDraft.totalOrders ?? ""}
                            onChange={(e) => handleDraftChange(sale._id, "totalOrders", e.target.value)}
                            className="w-full max-w-[140px] px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-black focus:outline-none focus:border-black focus:bg-white"
                            inputMode="numeric"
                          />
                        ) : (
                          <span className="text-gray-900">{sale.totalOrders}</span>
                        )}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          {isEditing ? (
                            <>
                              <button
                                type="button"
                                disabled={savingId === sale._id}
                                onClick={() => handleSaveRow(sale)}
                                className={`px-4 py-2 rounded-xl font-semibold text-xs transition-all ${savingId === sale._id
                                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                                  : "bg-blue-600 hover:bg-blue-700 text-white active:scale-[0.98]"
                                  }`}
                              >
                                {savingId === sale._id ? "Saving…" : "Save"}
                              </button>
                              <button
                                type="button"
                                disabled={savingId === sale._id}
                                onClick={cancelEdit}
                                className="px-4 py-2 rounded-xl font-semibold text-xs border border-gray-200 text-gray-700 hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => beginEdit(sale)}
                                className="p-2 text-gray-600 hover:bg-gray-50 rounded-xl border border-transparent hover:border-gray-200"
                                title="Edit row"
                                aria-label="Edit"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                  />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleteTarget({ id: sale._id, dateLabel: formatSaleDate(sale.saleDate) })}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-xl"
                                title="Remove record"
                                aria-label="Delete"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td colSpan={4} className="pb-3 pt-0 text-[11px] text-gray-400">
                        Last updated by <span className="text-gray-600">{who}</span> on {when}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        )}

        {!loading && totalCount > 0 ? (
          <div className="mt-2 text-xs text-gray-500">
            Showing {sales.length} of {totalCount} records
          </div>
        ) : null}

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/60 border-0 cursor-default"
            onClick={() => setDeleteTarget(null)}
            aria-label="Close dialog backdrop"
          />
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden p-8 text-center">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Remove sale row?</h3>
            <p className="text-sm text-gray-500 mb-8">
              This will delete the record for <span className="font-semibold text-gray-900">{deleteTarget.dateLabel}</span>.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-3 bg-gray-50 hover:bg-gray-100 text-gray-700 font-semibold rounded-xl border border-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSoftDelete}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OtherAppSaleHistory;
